import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { AlertTriangle } from 'lucide-react';
import { updateUserLocationStatus } from '../helpers/locationStatusApi';
import { locationStatusKey } from '../helpers/adminLocationStatus';
import { areLocationScopesEqual } from '../helpers/locationScopes';

export default function UserDataReview({ targetEmail, locationScope, statuses = [], loading, error, accessToken, onRefresh, disabled = false }) {
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const context = `${accessToken}:${targetEmail}:${locationStatusKey(locationScope)}`;
  const currentContext = useRef(context);
  const generation = useRef(0);
  const submission = useRef(null);
  if (currentContext.current !== context) {
    currentContext.current = context;
    generation.current += 1;
    submission.current = null;
  }
  const status = statuses.find((entry) => areLocationScopesEqual(entry.locationScope, locationScope));
  const pendingCount = Number(status?.pendingRequestsCount || 0);
  const current = status?.status === 'up_to_date';
  const unavailable = saving || loading || disabled || Boolean(error) || !targetEmail || !locationScope || !status;
  const warning = `Este usuario tiene ${pendingCount} solicitud(es) pendiente(s). Deben resolverse antes de confirmar que sus datos están al día.`;

  useEffect(() => {
    generation.current += 1;
    submission.current = null;
    setSaving(false);
    setFeedback(null);
    return () => { generation.current += 1; };
  }, [context]);

  async function save(desiredStatus) {
    if (unavailable || submission.current !== null || (desiredStatus === 'up_to_date' && pendingCount)) return;
    const savedGeneration = generation.current;
    submission.current = savedGeneration;
    setSaving(true);
    setFeedback(null);
    try {
      const result = await updateUserLocationStatus(targetEmail, locationScope, desiredStatus, accessToken);
      if (generation.current !== savedGeneration) return;
      setFeedback({ type: 'success', message: result.message || 'Se guardó la revisión de este usuario.' });
    } catch (saveError) {
      if (generation.current !== savedGeneration) return;
      setFeedback({ type: 'error', message: saveError.message || 'No se pudo guardar la revisión.' });
    } finally {
      if (generation.current === savedGeneration) {
        try { await onRefresh(); } catch { /* Parent reports lookup failures. */ }
        if (generation.current === savedGeneration) {
          submission.current = null;
          setSaving(false);
        }
      }
    }
  }

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Stack spacing={1}>
        <Typography variant="subtitle2">Datos correctos</Typography>
        <Typography variant="body2" color={current ? 'success.main' : 'warning.main'}>
          {loading ? 'Consultando…' : error ? 'No disponible' : current ? 'Todo está al día' : 'Requiere revisión'}
        </Typography>
        <Typography variant="body2" color="text.secondary">La revisión se guarda solo para este usuario en la sede seleccionada.</Typography>
        <Stack direction="row" alignItems="center" spacing={1} useFlexGap flexWrap="wrap">
          <Button variant="contained" color="success" disabled={unavailable || pendingCount > 0} onClick={() => save('up_to_date')}>
            Todo está al día
          </Button>
          {pendingCount > 0 && <Tooltip title={warning} arrow><Box component="span" tabIndex={0} role="img" aria-label={warning} sx={{ display: 'inline-flex', color: 'warning.main' }}><AlertTriangle size={20} /></Box></Tooltip>}
          <Button color="warning" disabled={unavailable} onClick={() => save('needs_review')}>Requiere revisión</Button>
        </Stack>
        {error && <Alert severity="warning">{error}</Alert>}
        {feedback && <Alert severity={feedback.type}>{feedback.message}</Alert>}
      </Stack>
    </Paper>
  );
}
